import React, { createContext, useContext, useEffect, useCallback, useMemo } from 'react';
import { useLocalStorage } from '../hooks/useLocalStorage';

const ThemeContext = createContext({
  theme: 'dark',
  toggleTheme: () => {},
  setTheme: () => {},
});

export const useTheme = () => useContext(ThemeContext);

function getSystemTheme() {
  if (typeof window === 'undefined') return 'dark';
  return window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
}

export function ThemeProvider({ children }) {
  const [theme, setThemeStored] = useLocalStorage('logitslab-theme', null);
  const resolved = theme === 'light' || theme === 'dark' ? theme : getSystemTheme();

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', resolved);
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) {
      meta.setAttribute('content', resolved === 'light' ? '#F7F6F2' : '#0E1110');
    }
  }, [resolved]);

  const setTheme = useCallback((next) => {
    setThemeStored(next);
  }, [setThemeStored]);

  const toggleTheme = useCallback(() => {
    setThemeStored(resolved === 'dark' ? 'light' : 'dark');
  }, [resolved, setThemeStored]);

  const value = useMemo(
    () => ({ theme: resolved, toggleTheme, setTheme }),
    [resolved, toggleTheme, setTheme]
  );

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}
